/**
 * Phase 18.2 — Web Push client-side helper.
 *
 * Encapsulates the (permission → service-worker register → PushManager
 * subscribe → POST /api/push/subscribe) flow. Uses the VAPID public key
 * supplied at build time via `REACT_APP_VAPID_PUBLIC_KEY`.
 */
import { api } from "@/lib/api";

const VAPID_PUB = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    !!VAPID_PUB
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function ensurePermission() {
  if (!("Notification" in window)) throw new Error("Notifications not supported");
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const p = await Notification.requestPermission();
  return p;
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) throw new Error("Service workers not supported");
  const reg = await navigator.serviceWorker.register("/sw-push.js");
  return reg;
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("Web push isn't supported in this browser");
  const perm = await ensurePermission();
  if (perm !== "granted") throw new Error("Notification permission denied");
  const reg = await ensureServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUB),
    });
  }
  const raw = sub.toJSON();
  const { data } = await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent,
  });
  return data;
}

export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) return { ok: false };
  const reg = await navigator.serviceWorker.getRegistration("/sw-push.js");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => null);
  await api.post("/push/unsubscribe", { endpoint }).catch(() => null);
  return { ok: true };
}

export async function listSubscriptions() {
  const { data } = await api.get("/push/subscriptions");
  return data;
}
